import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Loader2,
  BookOpen,
  Shield,
  AlertTriangle,
  CheckCircle,
  Layers
} from 'lucide-react';
import { useSocket } from '../../SocketContext';
import DeviceGroupManager from './DeviceGroupManager';
import DeviceGallery from './DeviceGallery';
import styles from '../CSS/ClassesComponent.module.css';

const ClassesComponent = ({ userRole, granularPermissions }) => {
  const { socket } = useSocket();
  const [devices, setDevices] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [matchingGroups, setMatchingGroups] = useState([]);
  const [selectedDeviceUDIDs, setSelectedDeviceUDIDs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [animationKey, setAnimationKey] = useState(0);
  const [showingAllDevices, setShowingAllDevices] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Grupos predefinidos originales
  const predefinedGroups = [
    { id: 'DBH', name: 'DBH', icon: <BookOpen size={16} /> },
    { id: 'LH', name: 'LH', icon: <BookOpen size={16} /> },
    { id: 'Botiquin', name: 'Botiquín', icon: <Shield size={16} /> }
  ];

  // ⭐ MEJORADO: Filtrar grupos según permisos granulados (rol + grupos)
  const availableGroups = useMemo(() => {
    console.log('🔍 Verificando permisos granulados de clases...', granularPermissions);

    // Si no hay permisos granulados cargados, usar rol antiguo
    if (!granularPermissions) {
      console.log('⚠️ Usando permisos del rol antiguo (fallback)');

      if (!userRole || !userRole.classPermissions) {
        console.log('✅ Sin restricciones, mostrando todos los grupos');
        return predefinedGroups;
      }

      const { allowedGroups, canManageAll } = userRole.classPermissions;

      if (canManageAll) {
        console.log('✅ Acceso total a grupos (canManageAll)');
        return predefinedGroups;
      }

      if (!Array.isArray(allowedGroups)) {
        console.log('✅ Sin restricciones específicas, mostrando todos los grupos');
        return predefinedGroups;
      }

      if (allowedGroups.length === 0) {
        console.log('🚫 Sin acceso a ningún grupo');
        return [];
      }

      const filtered = predefinedGroups.filter(group =>
        allowedGroups.includes(group.id)
      );

      console.log('🔒 Grupos filtrados (rol):', filtered.map(g => g.id));
      return filtered;
    }

    // ✅ USAR PERMISOS GRANULADOS (rol + grupos)
    const { allowedGroups, canManageAll } = granularPermissions.classPermissions;

    console.log('📋 Permisos granulados de clase:', { allowedGroups, canManageAll });

    // Si puede gestionar todos, mostrar todos
    if (canManageAll) {
      console.log('✅ Acceso total a grupos (canManageAll)');
      return predefinedGroups;
    }

    // Si allowedGroups no existe o es null/undefined (no array), mostrar todos
    if (!Array.isArray(allowedGroups)) {
      console.log('✅ Sin restricciones específicas, mostrando todos los grupos');
      return predefinedGroups;
    }

    // Si allowedGroups es un array vacío [], significa SIN ACCESO
    if (allowedGroups.length === 0) {
      console.log('🚫 Sin acceso a ningún grupo');
      return [];
    }

    // Filtrar solo los grupos permitidos
    const filtered = predefinedGroups.filter(group =>
      allowedGroups.includes(group.id)
    );

    console.log('🔒 Grupos filtrados (granular):', filtered.map(g => g.id));
    return filtered;
  }, [granularPermissions, userRole]);

  const deviceNamesMap = useMemo(() => {
    const map = {};
    devices.forEach(device => {
      map[device.UDID] = device.name;
    });
    return map;
  }, [devices]);

  const fetchDevicesAndGroups = async () => {
    try {
      setLoading(true);
      const [devicesResponse, groupsResponse] = await Promise.all([
        axios.get('https://truebavirtual.onrender.com/api/jamf/devices', {
          params: { hasOwner: true }
        }),
        axios.get('https://truebavirtual.onrender.com/api/jamf/groups')
      ]);

      const jamfDevices = devicesResponse.data.devices || [];

      // ✅ MEJORA: Combinar datos de Jamf con Firebase para tener info de restricciones
      // NOTA: No usamos onSnapshot aquí para evitar lecturas masivas
      // Los estados individuales se leen en DeviceGroupManager cuando se seleccionan

      setDevices(jamfDevices);
      setGroups(groupsResponse.data.deviceGroups || []);
      setLoading(false);
      setIsInitialLoad(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevicesAndGroups();
  }, []);

  // ✅ Socket.IO - Escuchar eventos en tiempo real (reemplaza Firebase onSnapshot)
  useEffect(() => {
    if (!socket) return;

    console.log('🔌 ClassesComponent: Configurando listeners de Socket.IO');

    // Evento cuando un dispositivo expira automáticamente
    const handleDeviceExpired = (data) => {
      console.log('📡 Socket.IO: Dispositivo expirado recibido', data);
      reloadDevices();
    };

    // Evento cuando un dispositivo pausado se reanuda automáticamente
    const handleDeviceResumed = (data) => {
      console.log('📡 Socket.IO: Dispositivo reanudado recibido', data);
      reloadDevices();
    };

    // Evento cuando se añaden dispositivos a un grupo
    const handleDevicesAddedToGroup = (data) => {
      console.log('📡 Socket.IO: Dispositivos añadidos a grupo', data);
      reloadDevices();
    };

    // Evento cuando se remueven dispositivos de un grupo
    const handleDevicesRemovedFromGroup = (data) => {
      console.log('📡 Socket.IO: Dispositivos removidos de grupo', data);
      reloadDevices();
    };

    // Registrar listeners
    socket.on('device-expired', handleDeviceExpired);
    socket.on('device-resumed', handleDeviceResumed);
    socket.on('jamf:devices-added-to-group', handleDevicesAddedToGroup);
    socket.on('jamf:devices-removed-from-group', handleDevicesRemovedFromGroup);

    // Cleanup
    return () => {
      socket.off('device-expired', handleDeviceExpired);
      socket.off('device-resumed', handleDeviceResumed);
      socket.off('jamf:devices-added-to-group', handleDevicesAddedToGroup);
      socket.off('jamf:devices-removed-from-group', handleDevicesRemovedFromGroup);
      console.log('🔌 ClassesComponent: Listeners de Socket.IO desregistrados');
    };
  }, [socket]);

  const handleGroupChange = groupId => {
    setSelectedGroup(groupId);
    setSelectedDeviceUDIDs([]);
    setShowingAllDevices(false);

    if (groupId === '') {
      setMatchingGroups([]);
      return;
    }

    const matchedGroups = groups.filter(group => {
      if (['LH', 'DBH'].includes(groupId)) {
        return new RegExp(`.*\\d.*${groupId}.*|.*${groupId}.*\\d.*`).test(
          group.name
        );
      }
      return group.name.includes(groupId);
    });

    setMatchingGroups(matchedGroups);
    setAnimationKey(prevKey => prevKey + 1);
  };

  const showAllDevicesInGroup = () => {
    setShowingAllDevices(true);
    setSelectedDeviceUDIDs([]);
    setAnimationKey(prevKey => prevKey + 1);
  };

  const toggleDeviceSelection = deviceId => {
    setSelectedDeviceUDIDs(prevUDIDs => {
      if (prevUDIDs.includes(deviceId)) {
        return prevUDIDs.filter(id => id !== deviceId);
      } else {
        return [...prevUDIDs, deviceId];
      }
    });
  };
  
  const handleSelectAll = (deviceUDIDs) => {
    setSelectedDeviceUDIDs(prev => {
      if (deviceUDIDs.every(id => prev.includes(id))) {
        return prev.filter(id => !deviceUDIDs.includes(id));
      }
      
      const newSelection = [...prev];
      deviceUDIDs.forEach(id => {
        if (!newSelection.includes(id)) {
          newSelection.push(id);
        }
      });
      return newSelection;
    });
  };
  
  const handleClearSelection = () => {
    setSelectedDeviceUDIDs([]);
  };

  const filteredDevices = selectedGroup && selectedGroup !== ''
    ? showingAllDevices
      ? devices.filter(device => 
          device.groupIds && 
          matchingGroups.some(group => device.groupIds.includes(group.id.toString()))
        )
      : devices.filter(
          device => device.groupIds && device.groupIds.includes(selectedGroup)
        )
    : [];

  const nameCount = {};
  filteredDevices.forEach(device => {
    const name = device.name;
    if (name in nameCount) {
      nameCount[name].count += 1;
    } else {
      nameCount[name] = { count: 1, devices: [] };
    }
    nameCount[name].devices.push(device);
  });

  const uniqueDevices = Object.values(nameCount).flatMap(({ count, devices }) => {
    if (count > 1) {
      const botiquinDevices = devices.filter(
        device => device.groupIds && device.groupIds.includes('130')
      );
      return botiquinDevices.length > 0 ? botiquinDevices : [devices[0]];
    }
    return devices.slice(0, 1);
  });

  const reloadDevices = async () => {
    setLoading(true);
    await fetchDevicesAndGroups();
  };

  const isGroupSelected = selectedGroup !== '';
  const isClassSelected =
    isGroupSelected &&
    (showingAllDevices || 
      (matchingGroups.length > 0 &&
      matchingGroups.some(group => group.id.toString() === selectedGroup)));

  if (isInitialLoad && loading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader2 size={40} className={styles.loadingSpinner} />
        <p>Cargando dispositivos...</p>
      </div>
    );
  }

  if (error && !isInitialLoad)
    return (
      <div className={styles.errorContainer}>
        <AlertTriangle size={40} className={styles.errorIcon} />
        <p className={styles.errorMessage}>Error: {error}</p>
        <button className={styles.retryButton} onClick={reloadDevices}>
          Reintentar
        </button>
      </div>
    );

  return (
    <div className={styles.classesContainer}>
      <div className={styles.groupHeader}>
        <h2 className={styles.sectionTitle}>Grupos</h2>
        <div className={styles.groupSelector}>
          {availableGroups.map(group => (
            <button
              key={group.id}
              onClick={() => handleGroupChange(group.id)}
              className={`${styles.groupButton} ${
                selectedGroup === group.id ? styles.selected : ''
              }`}
            >
              {group.icon}
              <span>{group.name}</span>
            </button>
          ))}
        </div>
      </div>

      {matchingGroups.length > 0 && (
        <div className={styles.groupHeader}>
          <h2 className={styles.sectionTitle}>Clases Disponibles</h2>
          <div className={styles.matchingGroups}>
            {matchingGroups.length > 1 && (
              <button
                onClick={showAllDevicesInGroup}
                className={`${styles.groupButton} ${
                  showingAllDevices ? styles.selected : ''
                }`}
              >
                <Layers size={16} />
                <span>Todos</span>
              </button>
            )}
            
            {matchingGroups.map(group => (
              <button
                key={group.id}
                onClick={() => {
                  setSelectedGroup(group.id.toString());
                  setSelectedDeviceUDIDs([]);
                  setShowingAllDevices(false);
                  setAnimationKey(prevKey => prevKey + 1);
                }}
                className={`${styles.groupButton} ${
                  selectedGroup === group.id.toString() && !showingAllDevices ? styles.selected : ''
                }`}
              >
                <BookOpen size={16} />
                <span>{group.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {uniqueDevices.length > 0 ? (
        <>
          <DeviceGroupManager
            selectedDeviceUDIDs={selectedDeviceUDIDs}
            onReload={reloadDevices}
            devices={devices}
            deviceNames={deviceNamesMap}
          />

          <DeviceGallery
            devices={uniqueDevices}
            selectedDeviceUDIDs={selectedDeviceUDIDs}
            toggleDeviceSelection={toggleDeviceSelection}
            animationKey={animationKey}
            onSelectAll={handleSelectAll}
            onClearSelection={handleClearSelection}
          />
        </>
      ) : (
        <div className={styles.emptyState}>
          {isGroupSelected && matchingGroups.length > 0 ? (
            <>
              <CheckCircle size={40} className={styles.emptyIcon} />
              <p>Selecciona una clase específica para ver sus dispositivos</p>
            </>
          ) : isGroupSelected && matchingGroups.length === 0 ? (
            <>
              <AlertTriangle size={40} className={styles.emptyIcon} />
              <p>No hay clases disponibles en este grupo</p>
              <button
                className={styles.resetButton}
                onClick={() => setSelectedGroup('')}
              >
                Volver a todos los grupos
              </button>
            </>
          ) : (
            <>
              <CheckCircle size={40} className={styles.emptyIcon} />
              <p>Selecciona un grupo para continuar</p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ClassesComponent;